import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { APP_NAME } from '../../shared/utils/appConfig';
import {
  FileText,
  Users,
  Settings,
  ClipboardList,
  BarChart2,
  Shield,
  Lock,
  Menu,
  X,
  ArrowRight,
  ChevronDown,
} from 'lucide-react';

// ─── Google Font injection ────────────────────────────────────────────────────
function useFonts() {
  useEffect(() => {
    const id = 'payslippro-fonts';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href =
      'https://fonts.googleapis.com/css2?family=Sora:wght@300;400;600;700;800&family=JetBrains+Mono:wght@400;500&display=swap';
    document.head.appendChild(link);
    return () => { /* leave font loaded */ };
  }, []);
}

// ─── Payslip SVG Mockup ───────────────────────────────────────────────────────
function PayslipMockup() {
  return (
    <div className="relative" style={{ perspective: '1200px' }}>
      {/* Shadow card behind */}
      <div
        className="absolute inset-0 rounded-2xl bg-indigo-500/20 blur-2xl"
        style={{ transform: 'translate(12px, 16px) rotateY(-4deg) rotateX(2deg)' }}
      />
      {/* Main card */}
      <div
        className="relative bg-white rounded-2xl overflow-hidden shadow-2xl border border-white/10"
        style={{
          transform: 'rotateY(-4deg) rotateX(2deg)',
          transformStyle: 'preserve-3d',
          fontFamily: 'JetBrains Mono, monospace',
        }}
      >
        {/* Card header */}
        <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-indigo-200 text-xs uppercase tracking-widest mb-0.5">Bulletin de Paie</p>
              <p className="text-white font-semibold text-sm">Caritas Bénin — ONG</p>
            </div>
            <div className="text-right">
              <p className="text-indigo-200 text-xs">Période</p>
              <p className="text-white font-semibold text-sm">Mars 2026</p>
            </div>
          </div>
        </div>

        {/* Employee info strip */}
        <div className="bg-indigo-50 px-6 py-2.5 border-b border-indigo-100 flex items-center justify-between">
          <div>
            <p className="text-xs text-slate-500">Employé</p>
            <p className="text-slate-800 text-sm font-semibold">Marie Kouassi</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-500">Matricule</p>
            <p className="text-slate-800 text-sm font-mono">BEN-0042</p>
          </div>
        </div>

        {/* Payslip table */}
        <div className="px-6 py-4">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left text-slate-400 font-medium pb-2 uppercase tracking-wider text-[10px]">Rubrique</th>
                <th className="text-right text-slate-400 font-medium pb-2 uppercase tracking-wider text-[10px]">Base</th>
                <th className="text-right text-slate-400 font-medium pb-2 uppercase tracking-wider text-[10px]">Montant</th>
              </tr>
            </thead>
            <tbody>
              {[
                { label: 'Salaire de base', base: '200 000', montant: '200 000', type: 'gain' },
                { label: 'Prime de transport', base: '—', montant: '15 000', type: 'gain' },
                { label: 'CNSS salarié', base: '3.6%', montant: '-7 200', type: 'deduction' },
                { label: 'ITS', base: 'Tranche', montant: '-12 400', type: 'deduction' },
              ].map((row, i) => (
                <tr key={i} className="border-b border-slate-50">
                  <td className="py-2 text-slate-700">{row.label}</td>
                  <td className="py-2 text-right text-slate-400">{row.base}</td>
                  <td className={`py-2 text-right font-medium ${row.type === 'deduction' ? 'text-red-500' : 'text-slate-700'}`}>
                    {row.montant}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Net à payer */}
          <div className="mt-4 bg-indigo-600 rounded-xl px-4 py-3 flex items-center justify-between">
            <span className="text-indigo-100 text-sm font-medium">NET À PAYER</span>
            <span className="text-white font-bold text-lg" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              195 400 <span className="text-indigo-300 text-xs font-normal">XOF</span>
            </span>
          </div>

          {/* VPS badge */}
          <div className="mt-3 flex items-center gap-2 text-xs text-slate-400">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
            VPS patronal : 7 200 XOF — Conforme CNSS 2026
          </div>
        </div>

        {/* Footer */}
        <div className="bg-slate-50 px-6 py-2.5 border-t border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded-sm bg-indigo-600 flex items-center justify-center">
              <FileText size={8} className="text-white" />
            </div>
            <span className="text-[10px] text-slate-400" style={{ fontFamily: 'Sora, sans-serif' }}>
              Généré par 
            </span>
          </div>
          <span className="text-[10px] text-emerald-500 font-medium">✓ Validé</span>
        </div>
      </div>
    </div>
  );
}

// ─── Features data ────────────────────────────────────────────────────────────
const features = [
  {
    icon: FileText,
    color: 'bg-primary-light text-primary',
    ring: 'ring-primary/20',
    title: 'Bulletins automatiques',
    desc: 'Génération individuelle ou en masse. Téléchargement PDF/DOCX en un clic. Tableau d\'état des rémunérations par période.',
  },
  {
    icon: Users,
    color: 'bg-violet-50 text-violet-600',
    ring: 'ring-violet-100',
    title: 'Gestion des employés',
    desc: 'Fiches complètes, primes personnalisées, oppositions sur salaire, import CSV en masse.',
  },
  {
    icon: Settings,
    color: 'bg-sky-50 text-sky-600',
    ring: 'ring-sky-100',
    title: 'Configuration paie',
    desc: 'Paramètres CNSS, ITS, VPS configurables. Barèmes ITS, catalogue de primes, simulateur temps réel.',
  },
  {
    icon: ClipboardList,
    color: 'bg-emerald-50 text-emerald-600',
    ring: 'ring-emerald-100',
    title: 'Déclarations sociales',
    desc: 'Déclaration CNSS et ITS mensuelle. Validation, marquage « Transmis » et suivi du statut.',
  },
  {
    icon: BarChart2,
    color: 'bg-amber-50 text-amber-600',
    ring: 'ring-amber-100',
    title: 'Rapports & exports',
    desc: 'Livre de paie, export SYSCOHADA / SYCEBNL (écritures comptables), rapport d\'audit complet des opérations.',
  },
/*   {
    icon: Shield,
    color: 'bg-teal-50 text-teal-600',
    ring: 'ring-teal-100',
    title: 'Multi-organisation',
    desc: 'Isolation totale des données par organisation. Chaque entité gère ses propres employés et configurations.',
  }, */
  {
    icon: Lock,
    color: 'bg-rose-50 text-rose-600',
    ring: 'ring-rose-100',
    title: 'Sécurité avancée',
    desc: 'Authentification 2FA (TOTP & e-mail), historique des sessions, détection des connexions suspectes.',
  },
];

// ─── Steps ────────────────────────────────────────────────────────────────────
const steps = [
  {
    num: '01',
    title: 'Configurez',
    desc: 'Paramétrez CNSS, ITS, VPS et vos barèmes en moins de 5 minutes. Définissez le catalogue de primes et les règles de calcul propres à votre organisation.',
    color: 'bg-primary',
  },
  {
    num: '02',
    title: 'Enregistrez vos employés',
    desc: 'Créez les fiches employées : informations personnelles, poste, salaire de base, primes, affectation projet. Importez en masse via CSV pour gagner du temps.',
    color: 'bg-violet-600',
  },
  {
    num: '03',
    title: 'Générez',
    desc: `Lancez la génération individuelle ou en masse. ${APP_NAME} calcule chaque rubrique automatiquement et détecte les pro-rata en cas d'embauche en cours de mois.`,
    color: 'bg-sky-600',
  },
  {
    num: '04',
    title: 'Exportez',
    desc: 'Téléchargez les bulletins PDF, validez vos déclarations CNSS/ITS et exportez vos écritures SYSCOHADA.',
    color: 'bg-emerald-600',
  },
];

// ─── Testimonials ─────────────────────────────────────────────────────────────
const testimonials = [
  {
    quote: `${APP_NAME} nous a fait gagner 2 jours entiers par mois sur la paie. Le simulateur est remarquable.`,
    name: 'Marie K.',
    role: 'DRH',
    org: 'ONG Solidarité Bénin',
    initials: 'MK',
    color: 'bg-primary-light text-primary-dark',
  },
  {
    quote: 'Nos déclarations CNSS sont toujours à temps. Zéro pénalité depuis que nous avons adopté l\'outil.',
    name: 'Franck A.',
    role: 'Comptable',
    org: 'PME Cotonou',
    initials: 'FA',
    color: 'bg-emerald-100 text-emerald-700',
  },
  {
    quote: 'Le tableau d\'état des rémunérations nous a permis de renegocier les salaires en toute transparence.',
    name: 'Isabelle M.',
    role: 'Directrice Administrative',
    org: 'Groupe Atlantique',
    initials: 'IM',
    color: 'bg-violet-100 text-violet-700',
  },
];

// ─── Main Component ───────────────────────────────────────────────────────────
export default function LandingPage() {
  useFonts();

  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const navLinks = [
    { label: 'Fonctionnalités', href: '#fonctionnalites' },
    { label: 'Comment ça marche', href: '#etapes' },
    { label: 'Témoignages', href: '#temoignages' },
  ];

  const handleAnchor = (href: string) => {
    setMenuOpen(false);
    if (href.startsWith('#')) {
      const el = document.querySelector(href);
      el?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div
      className="min-h-screen bg-surface text-content antialiased"
      style={{ fontFamily: 'Sora, sans-serif' }}
    >
      {/* ──────────────────────── NAVBAR ──────────────────────── */}
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-surface/95 backdrop-blur-sm shadow-sm border-b border-border'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center shadow-sm">
                <FileText size={16} className="text-white" />
              </div>
              <span
                className={`font-bold text-lg tracking-tight ${scrolled ? 'text-content' : 'text-white'}`}
              >
                {APP_NAME}
              </span>
            </div>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => (
                <button
                  key={link.label}
                  onClick={() => handleAnchor(link.href)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    scrolled
                      ? 'text-content-muted hover:text-primary hover:bg-primary/10'
                      : 'text-white/80 hover:text-white hover:bg-white/10'
                  }`}
                >
                  {link.label}
                </button>
              ))}
            </nav>

            {/* CTA buttons */}
            <div className="hidden md:flex items-center gap-3">
              <Link
                to="/auth/login"
                className={`text-sm font-medium px-4 py-2 rounded-lg border transition-colors ${
                  scrolled
                    ? 'border-border text-content hover:border-primary hover:text-primary'
                    : 'border-white/30 text-white hover:border-white/60 hover:bg-white/10'
                }`}
              >
                Se connecter
              </Link>
              {/* <Link
                to="/auth/register"
                className="text-sm font-semibold px-4 py-2 rounded-lg bg-primary text-content-inverted hover:bg-primary-hover transition-colors shadow-sm shadow-primary/20"
              >
                Essai gratuit
              </Link> */}
            </div>

            {/* Mobile hamburger */}
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className={`md:hidden p-2 rounded-lg transition-colors ${
                scrolled ? 'text-content hover:bg-surface-raised' : 'text-white hover:bg-white/10'
              }`}
            >
              {menuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden bg-surface border-t border-border shadow-lg">
            <div className="px-4 py-3 space-y-1">
              {navLinks.map((link) => (
                <button
                  key={link.label}
                  onClick={() => handleAnchor(link.href)}
                  className="w-full text-left px-3 py-2.5 rounded-lg text-content hover:bg-primary/10 hover:text-primary text-sm font-medium transition-colors"
                >
                  {link.label}
                </button>
              ))}
              <div className="pt-2 border-t border-border flex flex-col gap-2">
                <Link
                  to="/auth/login"
                  onClick={() => setMenuOpen(false)}
                  className="block text-center px-4 py-2.5 rounded-lg border border-border text-content text-sm font-medium"
                >
                  Se connecter
                </Link>
                <Link
                  to="/auth/register"
                  onClick={() => setMenuOpen(false)}
                  className="block text-center px-4 py-2.5 rounded-lg bg-primary text-content-inverted text-sm font-semibold"
                >
                  Essai gratuit
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* ──────────────────────── HERO ──────────────────────── */}
      <section
        id="hero"
        className="relative overflow-hidden min-h-screen flex flex-col"
        style={{
          background: 'linear-gradient(135deg, rgb(var(--color-sidebar-bg)) 0%, rgb(var(--color-primary-dark)) 40%, rgb(var(--color-primary)) 70%, rgb(var(--color-primary-dark)) 100%)',
        }}
      >
        {/* Grid pattern overlay */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.8) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
          }}
        />

        {/* Radial glow */}
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] rounded-full opacity-20 blur-3xl bg-primary pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-16 flex-1 flex flex-col">
          {/* Badge */}
          <div className="flex justify-center mb-8">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-primary/30 bg-primary/10 text-primary-muted text-xs font-medium tracking-wide">
              <span className="text-amber-400">✦</span>
              Conçu pour le marché béninois
            </span>
          </div>

          {/* Hero layout */}
          <div className="flex-1 grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: copy */}
            <div>
              <h1
                className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-white leading-[1.05] tracking-tight mb-6"
                style={{ fontFamily: 'Sora, sans-serif' }}
              >
                La paie de vos
                <br />
                équipes,{' '}
                <span className="relative">
                  <span className="text-primary-muted">enfin</span>
                  <span
                    className="absolute -bottom-1 left-0 right-0 h-[3px] rounded-full bg-primary-muted/50"
                  />
                </span>
                <br />
                sous contrôle.
              </h1>

              <p className="text-slate-300 text-lg leading-relaxed mb-8 max-w-xl">
                <span className="font-semibold text-white">{APP_NAME}</span> automatise vos bulletins, vos déclarations CNSS et ITS, et vos exports
                comptables SYSCOHADA / SYCEBNL.{' '}
                <span className="text-primary-muted font-medium">Conforme, sécurisé, {/* multi-organisation */}.</span>
              </p>

              <div className="flex flex-wrap gap-3 mb-12">
               {/*  <Link
                  to="/auth/register"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-primary hover:bg-primary-hover text-content-inverted font-semibold text-base transition-all shadow-lg shadow-primary/30 hover:shadow-primary/50 hover:-translate-y-0.5"
                >
                  Commencer gratuitement
                  <ArrowRight size={18} />
                </Link> */}
                <button
                  onClick={() => handleAnchor('#fonctionnalites')}
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl border border-white/20 text-white font-medium text-base hover:bg-white/10 hover:border-white/30 transition-all"
                >
                  Voir les fonctionnalités
                  <ChevronDown size={18} />
                </button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                {[
                  { value: '500+', label: 'Organisations' },
                  { value: '50 000+', label: 'Bulletins générés' },
                  { value: '99.9%', label: 'Conformité CNSS' },
                ].map((stat) => (
                  <div key={stat.label} className="border border-white/10 rounded-xl p-3 bg-white/5 backdrop-blur-sm">
                    <p
                      className="text-2xl font-extrabold text-white"
                      style={{ fontFamily: 'Sora, sans-serif' }}
                    >
                      {stat.value}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: payslip mockup */}
            <div className="hidden lg:flex items-center justify-center">
              <div className="w-full max-w-sm">
                <PayslipMockup />
              </div>
            </div>
          </div>
        </div>

        {/* Bottom wave */}
        <div className="relative h-16">
          <svg
            viewBox="0 0 1440 64"
            className="absolute bottom-0 w-full"
            style={{ fill: 'rgb(var(--color-surface))' }}
            preserveAspectRatio="none"
          >
            <path d="M0,64 C360,0 1080,0 1440,64 L1440,64 L0,64 Z" />
          </svg>
        </div>
      </section>

      {/* ──────────────────────── FEATURES ──────────────────────── */}
      <section id="fonctionnalites" className="py-24 bg-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold uppercase tracking-widest mb-4">
              Fonctionnalités
            </span>
            <h2 className="text-4xl sm:text-5xl font-extrabold text-content mb-4 tracking-tight">
              Tout ce dont vous avez besoin
            </h2>
            <p className="text-content-muted text-lg max-w-2xl mx-auto">
              De la génération du bulletin à l'export comptable, {APP_NAME} couvre l'intégralité de
              votre cycle de paie.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <div
                  key={i}
                  className={`group relative rounded-2xl p-5 border border-border bg-surface hover:border-primary hover:shadow-lg hover:shadow-primary/10 transition-all duration-300 ${
                    i === 6 ? 'sm:col-span-2 lg:col-span-1 xl:col-span-2' : ''
                  }`}
                >
                  <div
                    className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ring-4 ${f.color} ${f.ring} transition-transform group-hover:scale-110`}
                  >
                    <Icon size={20} />
                  </div>
                  <h3 className="font-bold text-content mb-2 text-base">{f.title}</h3>
                  <p className="text-content-muted text-sm leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ──────────────────────── HOW IT WORKS ──────────────────────── */}
      <section
        id="etapes"
        className="py-24 relative overflow-hidden bg-surface-raised"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-3 py-1 rounded-full bg-border text-content-muted text-xs font-semibold uppercase tracking-widest mb-4">
              Prise en main
            </span>
            <h2 className="text-4xl sm:text-5xl font-extrabold text-content tracking-tight">
              Prêt en 4 étapes
            </h2>
          </div>

          <div className="relative flex flex-col lg:flex-row items-stretch gap-6">
            {steps.map((step, i) => (
              <div key={i} className="flex-1 relative">
                {/* Connector line */}
                {i < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-12 left-full w-6 -translate-x-1/2 z-10">
                    <ArrowRight size={24} className="text-slate-300" />
                  </div>
                )}

                <div className="h-full bg-surface rounded-2xl p-7 border border-border shadow-sm hover:shadow-md transition-shadow">
                  <div
                    className={`w-12 h-12 rounded-2xl ${step.color} flex items-center justify-center mb-5 shadow-md`}
                  >
                    <span
                      className="text-white font-extrabold text-lg"
                      style={{ fontFamily: 'JetBrains Mono, monospace' }}
                    >
                      {step.num}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-content mb-3">{step.title}</h3>
                  <p className="text-content-muted text-sm leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ──────────────────────── TESTIMONIALS ──────────────────────── */}
      <section id="temoignages" className="py-24 bg-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold uppercase tracking-widest mb-4">
              Témoignages
            </span>
            <h2 className="text-4xl sm:text-5xl font-extrabold text-content tracking-tight">
              Ils nous font confiance
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div
                key={i}
                className="rounded-2xl p-7 border border-border bg-surface hover:border-primary hover:shadow-lg hover:shadow-primary/10 transition-all duration-300 flex flex-col"
              >
                {/* Stars */}
                <div className="flex gap-1 mb-5">
                  {Array.from({ length: 5 }).map((_, s) => (
                    <svg key={s} width="14" height="14" viewBox="0 0 20 20" fill="#f59e0b">
                      <path d="M10 1l2.4 7H20l-5.9 4.3 2.3 7L10 15.3l-6.4 4 2.3-7L0 8h7.6z" />
                    </svg>
                  ))}
                </div>

                <p className="text-content text-base leading-relaxed italic flex-1 mb-6">
                  «&thinsp;{t.quote}&thinsp;»
                </p>

                <div className="flex items-center gap-3 pt-4 border-t border-border">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${t.color}`}
                    style={{ fontFamily: 'Sora, sans-serif' }}
                  >
                    {t.initials}
                  </div>
                  <div>
                    <p className="font-semibold text-content text-sm">{t.name}</p>
                    <p className="text-content-muted text-xs">
                      {t.role} · {t.org}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ──────────────────────── FINAL CTA ──────────────────────── */}
      <section className="py-20 relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(135deg, rgb(var(--color-primary-dark)) 0%, rgb(var(--color-primary)) 50%, rgb(var(--color-primary-hover)) 100%)',
          }}
        />
        {/* Pattern overlay */}
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              'radial-gradient(circle, rgba(255,255,255,0.8) 1.5px, transparent 1.5px)',
            backgroundSize: '32px 32px',
          }}
        />

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
         {/*  <h2 className="text-4xl sm:text-5xl font-extrabold text-white mb-5 tracking-tight">
            Prêt à moderniser votre paie&nbsp;?
          </h2> 
          <p className="text-primary-muted text-lg mb-8 max-w-xl mx-auto">
            Rejoignez des centaines d'entreprises et d'ONG au Bénin qui font confiance à {APP_NAME}
            pour leur gestion de paie.
          </p>*/}
          <div className="flex flex-wrap items-center justify-center gap-3">
            {/* <Link
              to="/auth/register"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-surface text-primary font-bold text-base hover:bg-primary/10 transition-all shadow-xl shadow-primary/20 hover:-translate-y-0.5"
            >
              Commencer gratuitement
              <ArrowRight size={18} />
            </Link> */}
            <Link
              to="/auth/login"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl border border-white/30 text-white font-medium text-base hover:bg-white/10 hover:border-white/50 transition-all"
            >
              J'ai déjà un compte
            </Link>
          </div>
        </div>
      </section>

      {/* ──────────────────────── FOOTER ──────────────────────── */}
      <footer className="bg-slate-950 text-slate-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-8 mb-12">
            {/* Brand */}
            <div className="col-span-2">
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                  <FileText size={16} className="text-white" />
                </div>
                <span className="font-bold text-lg text-white tracking-tight">
                  {APP_NAME}
                </span>
              </div>
              <p className="text-sm leading-relaxed max-w-xs mb-4">
                La paie sans prise de tête. Conforme, rapide, fiable.
              </p>
              <p className="text-xs text-slate-500">
                Solution conçue pour le marché béninois
                <br />
                Conforme CNSS · ITS · VPS · SYSCOHADA
              </p>
            </div>

            {/* Produit */}
            <div>
              <h4 className="text-white font-semibold text-sm mb-4">Produit</h4>
              <ul className="space-y-2.5 text-sm">
                {['Bulletins de paie', 'Gestion employés', 'Configuration', 'Déclarations', 'Rapports'].map(
                  (item) => (
                    <li key={item}>
                      <a href="#fonctionnalites" className="hover:text-white transition-colors">
                        {item}
                      </a>
                    </li>
                  )
                )}
              </ul>
            </div>

            {/* Ressources */}
            <div>
              <h4 className="text-white font-semibold text-sm mb-4">Ressources</h4>
              <ul className="space-y-2.5 text-sm">
                {['Documentation', 'Guide CNSS 2026', 'Barèmes ITS', 'FAQ', 'Changelog'].map(
                  (item) => (
                    <li key={item}>
                      <span className="hover:text-white transition-colors cursor-pointer">{item}</span>
                    </li>
                  )
                )}
              </ul>
            </div>

            {/* Légal */}
            <div>
              <h4 className="text-white font-semibold text-sm mb-4">Légal</h4>
              <ul className="space-y-2.5 text-sm">
                {["Conditions d'utilisation", 'Politique de confidentialité', 'Sécurité', 'Contact'].map(
                  (item) => (
                    <li key={item}>
                      <span className="hover:text-white transition-colors cursor-pointer">{item}</span>
                    </li>
                  )
                )}
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-6 mb-4 text-center">
            <p className="text-xs text-slate-400">
              Conçu et développé par{' '}
              <span className="text-white font-semibold">Bernard Charlemagne KANMADOZO</span>
            </p>
            <p className="text-xs text-slate-500 mt-1">
              Tél.{' '}
              <a href="tel:+2290195951720" className="hover:text-primary transition-colors">+229 01 95 95 17 20</a>
              {' '}·{' '}
              <a href="tel:+2290161010861" className="hover:text-primary transition-colors">+229 01 61 01 08 61</a>
            </p>
          </div>

          <div className="border-t border-slate-800 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-slate-500">
              © 2026 {APP_NAME}. Tous droits réservés.
            </p>
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              Tous les systèmes opérationnels
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
